import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Hide media gallery and extract featured image for desktop
    if (!isMobileBreakpoint()) {
      const mediaGallery = productGrid.querySelector('.product-information__media');
      if (mediaGallery) {
        // Extract featured product image - featured image is typically the first slide
        let featuredImageSrc = '';
        
        // Try to find the first slide (featured image)
        const firstSlide = mediaGallery.querySelector('slideshow-slide:first-child, slideshow-slide[aria-hidden="false"]:first-of-type, .product-media-container:first-child');
        const featuredImage = firstSlide ? firstSlide.querySelector('img') : mediaGallery.querySelector('img');
        
        if (featuredImage) {
          // Try src first
          featuredImageSrc = featuredImage.src || featuredImage.getAttribute('src') || '';
          
          // Try srcset if src is not available
          if (!featuredImageSrc && featuredImage.srcset) {
            const srcsetParts = featuredImage.srcset.split(',');
            if (srcsetParts.length > 0) {
              featuredImageSrc = srcsetParts[0].trim().split(' ')[0];
            }
          }
          
          // Try data-src (lazy loading)
          if (!featuredImageSrc) {
            featuredImageSrc = featuredImage.getAttribute('data-src') || featuredImage.getAttribute('data-lazy-src') || '';
          }
          
          // Try to get from background-image style if it's a background
          if (!featuredImageSrc && featuredImage.closest('.product-media-container')) {
            const container = featuredImage.closest('.product-media-container');
            const bgImage = container.style.backgroundImage || window.getComputedStyle(container).backgroundImage;
            if (bgImage && bgImage !== 'none') {
              const urlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
              if (urlMatch) {
                featuredImageSrc = urlMatch[1];
              }
            }
          }
        }
        
        // Hide the media gallery
        mediaGallery.style.display = 'none';
        
        // Find the product title wrapper
        const productTitleElement = productGrid.querySelector('.view-product-title a');
        const titleWrapper = productTitleElement?.closest('.view-product-title');
        const productPrice = productGrid.querySelector('product-price');
        
        if (titleWrapper) {
          // Create a wrapper for title content (title + price)
          let titleContentWrapper = titleWrapper.querySelector('.quick-add-modal__title-content');
          if (!titleContentWrapper) {
            titleContentWrapper = document.createElement('div');
            titleContentWrapper.classList.add('quick-add-modal__title-content');
          }
          
          // Add featured image if available
          if (featuredImageSrc && !titleWrapper.querySelector('.quick-add-modal__title-image')) {
            const titleImage = document.createElement('img');
            titleImage.src = featuredImageSrc;
            titleImage.alt = productTitleElement?.textContent || '';
            titleImage.classList.add('quick-add-modal__title-image');
            
            // Insert image at the start of titleWrapper
            titleWrapper.insertBefore(titleImage, titleWrapper.firstChild);
          }
          
          // Move title link into content wrapper if not already there
          if (productTitleElement && !titleContentWrapper.contains(productTitleElement)) {
            titleContentWrapper.appendChild(productTitleElement);
          }
          
          // Move price into content wrapper below title
          if (productPrice && !titleContentWrapper.contains(productPrice)) {
            titleContentWrapper.appendChild(productPrice);
            
            // Calculate and add discount percentage
            QuickAddComponent.updateDiscountPercentage(productPrice);
          }
          
          // Insert content wrapper after image (or at the start if no image)
          const titleImage = titleWrapper.querySelector('.quick-add-modal__title-image');
          if (titleImage && !titleWrapper.contains(titleContentWrapper)) {
            titleWrapper.insertBefore(titleContentWrapper, titleImage.nextSibling);
          } else if (!titleWrapper.contains(titleContentWrapper)) {
            titleWrapper.appendChild(titleContentWrapper);
          }
        }
      }
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      
      // Extract featured product image for mobile
      let featuredImageSrc = '';
      const mediaGallery = productGrid.querySelector('.product-information__media');
      if (mediaGallery) {
        const firstSlide = mediaGallery.querySelector('slideshow-slide:first-child, slideshow-slide[aria-hidden="false"]:first-of-type, .product-media-container:first-child');
        const featuredImage = firstSlide ? firstSlide.querySelector('img') : mediaGallery.querySelector('img');

        if (featuredImage) {
          featuredImageSrc = featuredImage.src || featuredImage.getAttribute('src') || '';
          if (!featuredImageSrc && featuredImage.srcset) {
            const srcsetParts = featuredImage.srcset.split(',');
            if (srcsetParts.length > 0) {
              featuredImageSrc = srcsetParts[0].trim().split(' ')[0];
            }
          }
          if (!featuredImageSrc) {
            featuredImageSrc = featuredImage.getAttribute('data-src') || featuredImage.getAttribute('data-lazy-src') || '';
          }
        }
      }
      
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      // Create a wrapper for image and title (like desktop)
      const titleWrapper = document.createElement('div');
      titleWrapper.classList.add('view-product-title');

      // Add product image if available
      if (featuredImageSrc) {
        const titleImage = document.createElement('img');
        titleImage.src = featuredImageSrc;
        titleImage.alt = productTitle.textContent || '';
        titleImage.classList.add('quick-add-modal__title-image');
        titleWrapper.appendChild(titleImage);
      }

      // Create content wrapper for title and price
      const titleContentWrapper = document.createElement('div');
      titleContentWrapper.classList.add('quick-add-modal__title-content');
      titleContentWrapper.appendChild(productTitle);

      // Add price below title (like desktop)
      if (productPrice) {
        titleContentWrapper.appendChild(productPrice);
        // Calculate and add discount percentage for mobile
        QuickAddComponent.updateDiscountPercentage(productPrice);
      }

      titleWrapper.appendChild(titleContentWrapper);
      productHeader.appendChild(titleWrapper);
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Extract drawer initialization scripts before removing them
    // We'll execute them after morphing to reuse the exact PDP functionality
    // This includes size guide (with calculate functionality), style guide, and flow guide functionality
    // The section_id is preserved from the fetched product page, ensuring correct product data
    const drawerScripts = productGrid.querySelectorAll('script');
    const extractedDrawerScripts = [];
    let sizeGuideScriptSkipped = false;
    let sizeGuideScriptFound = false;
    drawerScripts.forEach(script => {
      const scriptText = script.textContent || '';
      const scriptType = (script.getAttribute('type') || '').toLowerCase();
      const isJavaScriptType = !scriptType || scriptType === 'text/javascript' || scriptType === 'application/javascript';
      const hasLiquidTags = scriptText.includes('{%') || scriptText.includes('%}') || scriptText.includes('{{') || scriptText.includes('}}');

      
      if (isJavaScriptType && !hasLiquidTags) {
        if (scriptText.includes('initSizeGuideDrawer') || 
            scriptText.includes('initStyleGuideDrawer') ||
            scriptText.includes('size-guide-drawer') ||
            scriptText.includes('style-guide-drawer')) {
          // Extract size guide and style guide scripts only.
          // Flow guide uses global event handling in quick-add, so we skip eval.
          extractedDrawerScripts.push(scriptText);
          if (scriptText.includes('initSizeGuideDrawer')) {
            sizeGuideScriptFound = true;
          }
        }
      } else if (scriptText.includes('initSizeGuideDrawer')) {
        sizeGuideScriptSkipped = true;
      }

      // Always remove scripts from the fetched HTML to prevent unintended execution
      // (some scripts can contain Liquid tags, causing syntax errors).
      script.remove();
    });

    morph(modalContent, productGrid);

    // Ensure no inline scripts remain in the modal after morphing.
    // Some scripts can include Liquid tags which will throw parse errors when executed.
    modalContent.querySelectorAll('script').forEach(script => script.remove());

    this.#syncVariantSelection(modalContent);
    
    // Add selected variant display and absorbency section after DOM is ready
    requestAnimationFrame(() => {
      QuickAddComponent.updateVariantPickerSelected(modalContent);
      QuickAddComponent.addAbsorbencySection(modalContent, productGrid);
      
    console.log('[quick-add] drawer scripts', {
      totalScripts: drawerScripts.length,
      extracted: extractedDrawerScripts.length,
      sizeGuideScriptFound: sizeGuideScriptFound,
      sizeGuideScriptSkipped: sizeGuideScriptSkipped
    });

    // Execute extracted drawer initialization scripts to reuse exact PDP functionality
      // These scripts are IIFEs that call initSizeGuideDrawer/initStyleGuideDrawer
      // They will find the drawer elements by ID in the morphed content
      extractedDrawerScripts.forEach(scriptText => {
        try {
          // Execute the script directly - it's already an IIFE that will run immediately
          // The script uses document.getElementById which will find elements in the modal
          eval(scriptText);
        } catch (error) {
          console.warn('Error executing drawer initialization script:', error);
        }
      });
    });
  }
  
  /**
   * Static helper method to calculate and update discount percentage
   * @param {Element} productPrice - The product-price element
   */
  static updateDiscountPercentage(productPrice) {
    const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;
    
    const compareAtPriceEl = priceContainer.querySelector('.compare-at-price');
    const currentPriceEl = priceContainer.querySelector('.price');
    
    // Remove existing discount badge
    const existingDiscount = priceContainer.querySelector('.discount-percentage');
    if (existingDiscount) {
      existingDiscount.remove();
    }
    
    // Calculate and add discount if compare-at-price exists
    if (compareAtPriceEl && currentPriceEl) {
      // Extract numeric values from price strings
      const compareAtText = compareAtPriceEl.textContent.trim();
      const currentPriceText = currentPriceEl.textContent.trim();
      
      // Try to extract numbers (handle various currency formats)
      const compareAtMatch = compareAtText.match(/[\d,]+\.?\d*/);
      const currentPriceMatch = currentPriceText.match(/[\d,]+\.?\d*/);
      
      if (compareAtMatch && currentPriceMatch) {
        const compareAtValue = parseFloat(compareAtMatch[0].replace(/,/g, ''));
        const currentPriceValue = parseFloat(currentPriceMatch[0].replace(/,/g, ''));
        
        if (compareAtValue > currentPriceValue && compareAtValue > 0) {
          const discountPercent = Math.round(((compareAtValue - currentPriceValue) / compareAtValue) * 100);
          
          if (discountPercent > 0) {
            const discountBadge = document.createElement('span');
            discountBadge.className = 'discount-percentage';
            discountBadge.setAttribute('aria-label', `${discountPercent}% off`);
            discountBadge.textContent = `${discountPercent}% OFF`;
            priceContainer.appendChild(discountBadge);
          }
        }
      }
    }
  }

  /**
   * Static helper method to add absorbency section above variant picker
   * @param {Element} modalContent - The modal content element
   * @param {Element} productGrid - The product grid element (for getting product data)
   */
  static addAbsorbencySection(modalContent, productGrid) {
    // Find the variant picker to insert before it
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) return;
    
    // Find the flow guide drawer from the productGrid (already morphed into modal)
    const flowGuideDrawer = modalContent.querySelector('.flow-guide-drawer');
    
    // Get section ID from the flow guide drawer or variant picker
    let sectionId = null;
    if (flowGuideDrawer) {
      const drawerId = flowGuideDrawer.id; // e.g., "flow-guide-drawer-template--123..."
      sectionId = drawerId.replace('flow-guide-drawer-', '');
    }
    
    // If no flow guide drawer, try to get section ID from variant picker
    if (!sectionId) {
      sectionId = variantPicker.dataset.sectionId || 
                  modalContent.querySelector('product-form-component')?.dataset.sectionId;
    }
    
    // Check if absorbency section already exists (from morphed variant-picker)
    const existingAbsorbencySection = modalContent.querySelector('.quick-add-absorbency-section');
    if (existingAbsorbencySection) {
      // Wire up the Flow Guide button if section ID is available
      if (sectionId) {
        const flowGuideButton = existingAbsorbencySection.querySelector('[data-flow-guide-trigger]');
        if (flowGuideButton) {
          // Keep quick-add flow guide isolated to its own modal
          // Avoid adding PDP flow-guide class/trigger in quick-add context
          flowGuideButton.removeAttribute('data-drawer-trigger');
        }
      }
      return;
    }
    
    // Get product ID from variant picker or product form component
    const productId = variantPicker.dataset.productId || 
                     modalContent.querySelector('product-form-component')?.dataset.productId;
    if (!productId) return;
    
    const productUrl = variantPicker.dataset.productUrl || 
                      modalContent.querySelector('product-form-component')?.dataset.productUrl;
    if (!productUrl) return;
    
    // Fetch the product page HTML to check if absorbency section is rendered there
    fetch(productUrl)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const absorbencySection = doc.querySelector('.quick-add-absorbency-section');
        
        if (absorbencySection) {
          // Clone and insert the section
          const clonedSection = absorbencySection.cloneNode(true);
          const variantPickerParent = variantPicker.parentElement;
          if (variantPickerParent) {
            variantPickerParent.insertBefore(clonedSection, variantPicker);
          } else {
            variantPicker.insertAdjacentElement('beforebegin', clonedSection);
          }
          
          // Wire up the Flow Guide button if section ID is available
          if (sectionId) {
            const flowGuideButton = clonedSection.querySelector('[data-flow-guide-trigger]');
          if (flowGuideButton) {
            // Keep quick-add flow guide isolated to its own modal
            flowGuideButton.removeAttribute('data-drawer-trigger');
          }
          }
        } else {
          // If not in product page, fetch via snippet render
          const urlMatch = productUrl.match(/\/products\/([^?#]+)/);
          if (urlMatch) {
            const productHandle = urlMatch[1];
            QuickAddComponent.renderAbsorbencyFromMetafields(modalContent, variantPicker, productHandle, sectionId);
          }
        }
      })
      .catch(error => {
        console.warn('Quick Add: Could not fetch absorbency section', error);
      });
  }

  /**
   * Renders absorbency section from metafields data
   * @param {Element} modalContent - The modal content element
   * @param {Element} variantPicker - The variant picker element
   * @param {string} productHandle - The product handle
   * @param {string} sectionId - The section ID for flow guide drawer
   */
  static renderAbsorbencyFromMetafields(modalContent, variantPicker, productHandle, sectionId) {
    // Try to get metafields from product JSON API
    fetch(`/products/${productHandle}.js`)
      .then(response => response.json())
      .then(product => {
        // Metafields might not be in .js endpoint, try fetching full page
        // or check if they're in a script tag
        const scriptTags = document.querySelectorAll('script[type="application/json"][data-product-json]');
        for (const script of scriptTags) {
          try {
            const productData = JSON.parse(script.textContent);
            if (productData.id == product.id) {
              const metafields = productData.metafields || {};
              const absorbencyLabel = metafields['custom.absorbency']?.value || metafields['custom_absorbency']?.value;
              const absorbencyImage = metafields['custom.absorbency_image']?.value || metafields['custom_absorbency_image']?.value;
              
              if (absorbencyLabel || absorbencyImage) {
                QuickAddComponent.createAbsorbencySection(modalContent, variantPicker, absorbencyLabel, absorbencyImage);
              }
              return;
            }
          } catch (e) {
            // Continue to next script tag
          }
        }
      })
      .catch(error => {
        console.warn('Quick Add: Could not fetch product data for absorbency', error);
      });
  }

  /**
   * Creates and inserts the absorbency section
   * @param {Element} modalContent - The modal content element
   * @param {Element} variantPicker - The variant picker element
   * @param {string} label - The absorbency label
   * @param {string} imageUrl - The absorbency image URL
   */
  static createAbsorbencySection(modalContent, variantPicker, label, imageUrl) {
    const absorbencySection = document.createElement('div');
    absorbencySection.className = 'quick-add-absorbency-section';
    
    const absorbencyContent = document.createElement('div');
    absorbencyContent.className = 'quick-add-absorbency-content';
    
    if (label) {
      const labelEl = document.createElement('div');
      labelEl.className = 'quick-add-absorbency-label';
      labelEl.textContent = label;
      absorbencyContent.appendChild(labelEl);
    }
    
    if (imageUrl) {
      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'quick-add-absorbency-image-wrapper';
      
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = label || 'Absorbency';
      image.className = 'quick-add-absorbency-image';
      image.loading = 'lazy';
      
      imageWrapper.appendChild(image);
      absorbencyContent.appendChild(imageWrapper);
    }
    
    absorbencySection.appendChild(absorbencyContent);
    
    const variantPickerParent = variantPicker.parentElement;
    if (variantPickerParent) {
      variantPickerParent.insertBefore(absorbencySection, variantPicker);
    } else {
      variantPicker.insertAdjacentElement('beforebegin', absorbencySection);
    }
  }

  /**
   * Static helper method to update variant picker selected values
   * Shared between QuickAddComponent and QuickAddDialog to avoid duplication
   * @param {Element} modalContent - The modal content element
   */
  static updateVariantPickerSelected(modalContent) {
    const variantPickers = modalContent.querySelectorAll('variant-picker');
    
    for (const variantPicker of variantPickers) {
      const fieldsets = variantPicker.querySelectorAll('fieldset');
      
      for (const fieldset of fieldsets) {
        const legend = fieldset.querySelector('legend');
        if (!legend) continue;
        
        const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
        if (!checkedInput) {
          // Remove selected value if no selection
          const selectedSpan = legend.querySelector('.variant-option__selected-value');
          if (selectedSpan) selectedSpan.remove();
          continue;
        }
        
        const selectedValue = checkedInput.value || checkedInput.getAttribute('aria-label') || '';
        if (!selectedValue) continue;
        
        // Check for existing span
        let selectedValueSpan = legend.querySelector('.variant-option__selected-value');
        const swatchSpan = legend.querySelector('.variant-option__swatch-value');
        
        if (swatchSpan) {
          // Update existing swatch value span (used for swatch variant style)
          swatchSpan.textContent = selectedValue;
        } else if (selectedValueSpan) {
          // Update existing selected value span
          selectedValueSpan.textContent = `: ${selectedValue}`;
        } else {
          // Create new span for selected value (used for buttons variant style)
          selectedValueSpan = document.createElement('span');
          selectedValueSpan.className = 'variant-option__selected-value';
          selectedValueSpan.textContent = `: ${selectedValue}`;
          legend.appendChild(selectedValueSpan);
        }
        
        // Set up MutationObserver only once per legend to re-add if removed by morph
        if (!legend.dataset.selectedObserver) {
          legend.dataset.selectedObserver = 'true';
          
          const observer = new MutationObserver(() => {
            // If selected value span was removed, re-add it
            if (!legend.querySelector('.variant-option__selected-value')) {
              const checked = fieldset.querySelector('input[type="radio"]:checked');
              if (checked) {
                const value = checked.value || checked.getAttribute('aria-label') || '';
                if (value) {
                  const newSpan = document.createElement('span');
                  newSpan.className = 'variant-option__selected-value';
                  newSpan.textContent = `: ${value}`;
                  legend.appendChild(newSpan);
                }
              }
            }
          });
          
          observer.observe(legend, { childList: true });
        }
      }
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    const nextHref = anchorElement.href;
    const nextTitleText = (anchorElement.textContent || '').trim();

    if (viewMoreDetailsLink) {
      viewMoreDetailsLink.href = nextHref;
      if (nextTitleText) viewMoreDetailsLink.textContent = nextTitleText;
    }

    if (mobileProductTitle) {
      mobileProductTitle.href = nextHref;
      if (nextTitleText) mobileProductTitle.textContent = nextTitleText;
    }

    const nextImageSrc = event.detail.resource?.featured_media?.preview_image?.src;
    if (nextImageSrc) {
      const titleWrapper = this.querySelector('.view-product-title');
      if (!titleWrapper) return;
      let titleImage = titleWrapper.querySelector('.quick-add-modal__title-image');
      if (!titleImage) {
        titleImage = document.createElement('img');
        titleImage.classList.add('quick-add-modal__title-image');
        titleWrapper.insertBefore(titleImage, titleWrapper.firstChild);
      }

      titleImage.src = nextImageSrc;
      if (nextTitleText) titleImage.alt = nextTitleText;
    }
  };

  /**
   * Handles variant update events to re-add selected variant display and update discount
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (/** @type {CustomEvent} */ event) => {
    // Only handle if the event came from within this dialog
    if (!event.target || !this.contains(event.target)) return;
    
    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!modalContent) return;
    
    // Wait for morph to complete, then update selected variant display and discount
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        QuickAddComponent.updateVariantPickerSelected(modalContent);
        
        // Update discount percentage for new variant price
        const productPrice = modalContent.querySelector('product-price');
        if (productPrice) {
          QuickAddComponent.updateDiscountPercentage(productPrice);
        }
      });
    });
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * Ensure Style Guide and Size Guide drawers also work when content is rendered
 * inside the Quick Add modal (fetched HTML doesn't execute inline <script> tags).
 * We use event delegation for opening/closing, and execute the original PDP scripts
 * to reuse all existing functionality (video, calculator, etc.).
 */
(function initGlobalGuideDrawers() {
  /**
   * Open style/size drawers - wrap in temporary dialog if opened from quick-add modal
   * to ensure they appear above the modal's top-layer on mobile
   * @param {HTMLElement} drawer
   * @param {HTMLElement} trigger
   */
  function openDrawer(drawer, trigger) {
    if (!drawer) return;

    // Check if trigger is inside quick-add modal
    const isInsideQuickAdd = trigger.closest('quick-add-dialog') !== null;

    if (trigger) {
      drawer._activeTrigger = trigger;
      drawer._activeVariantPicker = trigger.closest('variant-picker');
    }

    if (isInsideQuickAdd) {
      // For mobile: wrap drawer in a temporary native dialog to render in top-layer
      /** @type {HTMLDialogElement | null} */
      let dialogWrapper = null;
      
      // Check if drawer already has a wrapper stored
      if ('_dialogWrapper' in drawer && drawer._dialogWrapper instanceof HTMLDialogElement) {
        dialogWrapper = drawer._dialogWrapper;
      }
      
      // Create wrapper dialog if it doesn't exist
      if (!dialogWrapper) {
        dialogWrapper = document.createElement('dialog');
        dialogWrapper.className = 'guide-drawer-wrapper';
        dialogWrapper.style.cssText = 'border: none; padding: 0; margin: 0; background: transparent; max-width: 100%; max-height: 100%; width: 100%; height: 100%; overflow: visible;';
        
        // Store reference to wrapper on the drawer
        Object.defineProperty(drawer, '_dialogWrapper', {
          value: dialogWrapper,
          writable: true,
          configurable: true
        });
        
        // Move drawer into wrapper
        document.body.appendChild(dialogWrapper);
        dialogWrapper.appendChild(drawer);
      }
      
      // Open the wrapper dialog (puts it in top-layer)
      if (!dialogWrapper.open) {
        dialogWrapper.showModal();
      }
    } else {
      // For PDP: just append to body normally
      if (drawer.parentElement !== document.body) {
        document.body.appendChild(drawer);
      }
    }

    const anyDrawer = drawer;
    if (drawer.classList.contains('size-guide-drawer') && typeof anyDrawer._resetDrawerState === 'function') {
      try {
        anyDrawer._resetDrawerState();
      } catch (e) {
        console.error('Error resetting size guide drawer state', e);
      }
    }
    
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');

    // Load and play size guide video when opening from quick-add
    if (drawer.classList.contains('size-guide-drawer')) {
      const drawerId = drawer.id;
      const sectionId = drawerId.replace('size-guide-drawer-', '');
      /** @type {HTMLVideoElement | null} */
      const videoEl = drawer.querySelector('#size-guide-video-' + sectionId);
      if (!videoEl || !(videoEl instanceof HTMLVideoElement)) {
        return;
      }

      /** @type {HTMLVideoElement} */
      const video = videoEl;
      video.muted = true;
      video.setAttribute('muted', 'muted');

      const sources = video.querySelectorAll('source[data-src]');
      if (sources.length > 0) {
        sources.forEach((source) => {
          const srcValue = source.getAttribute('data-src');
          if (srcValue) {
            source.setAttribute('src', srcValue);
            source.removeAttribute('data-src');
          }
        });

        video.load();

        function attemptPlay() {
          if (video.readyState >= 2) {
            video.play().catch(() => {});
          }
        }

        video.addEventListener('canplay', attemptPlay, { once: true });
      }
    }
    
    // Attach direct event listeners to ensure close button works
    // Use a data attribute to track if we've already attached listeners
    if (!drawer.dataset.globalCloseHandlers) {
      const closeButton = drawer.querySelector('[data-drawer-close], .style-guide-drawer__close-button, .size-guide-drawer__close-button, .flow-guide-drawer__close-button');
      if (closeButton) {
        /** @param {Event} e */
        const handleClose = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          closeDrawer(drawer);
        };
        closeButton.addEventListener('click', handleClose, { capture: true, passive: false });
        closeButton.addEventListener('touchstart', handleClose, { capture: true, passive: false });
      }
      
      const overlay = drawer.querySelector('.style-guide-drawer__overlay, .size-guide-drawer__overlay, .flow-guide-drawer__overlay');
      if (overlay) {
        /** @param {Event} e */
        const handleOverlayClose = (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeDrawer(drawer);
        };
        overlay.addEventListener('click', handleOverlayClose, { capture: true, passive: false });
        overlay.addEventListener('touchstart', handleOverlayClose, { capture: true, passive: false });
      }
      
      drawer.dataset.globalCloseHandlers = 'true';
    }
  }

  /** @param {HTMLElement} drawer */
  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');

    const finishClose = () => {
      document.body.classList.remove('drawer-open');
      if ('_dialogWrapper' in drawer && drawer._dialogWrapper instanceof HTMLDialogElement) {
        const dialogWrapper = drawer._dialogWrapper;
        if (dialogWrapper.open) {
          dialogWrapper.close();
        }
      }
      if (drawer.classList.contains('size-guide-drawer')) {
        const drawerId = drawer.id;
        const sectionId = drawerId.replace('size-guide-drawer-', '');
        const video = drawer.querySelector('#size-guide-video-' + sectionId);
        if (video instanceof HTMLVideoElement) {
          video.pause();
          video.currentTime = 0;
        }
      }
    };

    // Wait for close transition (content-wrapper slide-out) before cleanup
    const contentWrapper = drawer.querySelector(
      '.style-guide-drawer__content-wrapper, .size-guide-drawer__content-wrapper'
    );
    if (contentWrapper) {
      const onTransitionEnd = (e) => {
        if (e.propertyName !== 'transform') return;
        contentWrapper.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(fallback);
        finishClose();
      };
      const fallback = setTimeout(() => {
        contentWrapper.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      }, 550);
      contentWrapper.addEventListener('transitionend', onTransitionEnd);
    } else {
      finishClose();
    }
  }

  /** @param {Event} event */
  function handleGuideTriggerEvent(event) {
      const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
      if (!target) return false;

      // Open style, size guide, or flow guide drawer
      const guideTrigger = target.closest('.style-guide-link[data-drawer-trigger], .size-guide-link[data-drawer-trigger], .absorbency-selector-block__flow-guide-link[data-drawer-trigger]');
        if (guideTrigger instanceof HTMLElement) {
        const isInsideQuickAdd = guideTrigger.closest('quick-add-dialog') !== null;
        // Let the dedicated quick-add flow guide modal handle its own trigger
        if (guideTrigger.classList.contains('quick-add-absorbency-flow-guide-link')) {
          return false;
        }
        if (!isInsideQuickAdd) return false;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const sectionId = guideTrigger.getAttribute('data-drawer-trigger');
        if (!sectionId) return true;

        let drawerId;
        if (guideTrigger.classList.contains('style-guide-link')) {
          drawerId = 'style-guide-drawer-' + sectionId;
        } else if (guideTrigger.classList.contains('size-guide-link')) {
          drawerId = 'size-guide-drawer-' + sectionId;
        } else if (guideTrigger.classList.contains('absorbency-selector-block__flow-guide-link')) {
          drawerId = 'flow-guide-drawer-' + sectionId;
        } else {
          return;
        }
        
        const drawerCandidates = document.querySelectorAll('[id="' + drawerId + '"]');
        let drawer = null;
        if (drawerCandidates.length === 1) {
          drawer = drawerCandidates.item(0);
        } else if (drawerCandidates.length > 1) {
          for (let i = 0; i < drawerCandidates.length; i++) {
            const candidate = drawerCandidates.item(i);
            if (candidate && candidate.dataset && candidate.dataset.sizeGuideInitialized === 'true') {
              drawer = candidate;
              break;
            }
          }
          if (!drawer) {
            for (let i = 0; i < drawerCandidates.length; i++) {
              const candidate = drawerCandidates.item(i);
              if (candidate && candidate.closest('dialog.guide-drawer-wrapper')) {
                drawer = candidate;
                break;
              }
            }
          }
          if (!drawer) drawer = drawerCandidates.item(0);
        }
        if (!drawer) return true;

        openDrawer(drawer, guideTrigger);
        return true;
      }
      return false;
  }

  document.addEventListener(
    'touchstart',
    (event) => {
      if (handleGuideTriggerEvent(event)) return;
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
      if (target) {
        const calculateBtn = target.closest('.size-guide-drawer__calculate-button');
        if (calculateBtn instanceof HTMLElement) {
          console.log('[size-guide] delegated calculate click', calculateBtn);
        }
      }
      if (handleGuideTriggerEvent(event)) return;
      if (!target) return;

      // Close drawers via close button or overlay
      // Check if click is on close button (including its children like SVG)
      const closeButton = target.closest('.style-guide-drawer__close-button, .size-guide-drawer__close-button, .flow-guide-drawer__close-button');
      if (closeButton instanceof HTMLElement) {
        const drawer = closeButton.closest('.style-guide-drawer, .size-guide-drawer, .flow-guide-drawer');
        if (drawer instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation(); // Prevent other handlers from running
          closeDrawer(drawer);
        }
        return;
      }
      
      // Also check for data-drawer-close attribute directly
      const closeBtnWithAttr = target.closest('[data-drawer-close]');
      if (closeBtnWithAttr instanceof HTMLElement) {
        const drawer = closeBtnWithAttr.closest('.style-guide-drawer, .size-guide-drawer, .flow-guide-drawer');
        if (drawer instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation(); // Prevent other handlers from running
          closeDrawer(drawer);
        }
        return;
      }

      const overlay = target.closest('.style-guide-drawer__overlay, .size-guide-drawer__overlay, .flow-guide-drawer__overlay');
      if (overlay instanceof HTMLElement) {
        const drawer = overlay.closest('.style-guide-drawer, .size-guide-drawer, .flow-guide-drawer');
        if (drawer instanceof HTMLElement) {
          event.preventDefault();
          event.stopPropagation();
          closeDrawer(drawer);
        }
      }
    },
    { capture: true }
  );

  // Close on Escape key when any drawer is open
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openDrawerEl = /** @type {HTMLElement | null} */ (
      document.querySelector('.style-guide-drawer[aria-hidden="false"], .size-guide-drawer[aria-hidden="false"], .flow-guide-drawer[aria-hidden="false"]')
    );
    if (!openDrawerEl) return;
    event.preventDefault();
    closeDrawer(openDrawerEl);
  });

  // Quick Add Flow Guide modal (same smooth open/close as style-guide-drawer)
  const quickAddFlowGuideDialog = document.getElementById('quick-add-flow-guide-modal');
  if (quickAddFlowGuideDialog instanceof HTMLDialogElement) {
    let flowGuideCloseTimeout = null;

    const closeQuickAddFlowGuide = () => {
      if (!quickAddFlowGuideDialog.open) return;
      if (flowGuideCloseTimeout) {
        clearTimeout(flowGuideCloseTimeout);
        flowGuideCloseTimeout = null;
      }
      quickAddFlowGuideDialog.classList.remove('is-open-animating');
      quickAddFlowGuideDialog.classList.remove('is-panel-visible');
      quickAddFlowGuideDialog.classList.add('is-closing');
      const panel = quickAddFlowGuideDialog.querySelector('.quick-add-flow-guide-modal__panel');

      const finishClose = () => {
        quickAddFlowGuideDialog.classList.remove('is-closing');
        quickAddFlowGuideDialog.classList.remove('is-open-animating');
        quickAddFlowGuideDialog.classList.remove('is-panel-visible');
        quickAddFlowGuideDialog.close();
        document.body.classList.remove('drawer-open');
      };

      if (panel) {
        const onTransitionEnd = (e) => {
          if (e.propertyName !== 'transform') return;
          panel.removeEventListener('transitionend', onTransitionEnd);
          clearTimeout(flowGuideCloseTimeout);
          finishClose();
        };
        flowGuideCloseTimeout = setTimeout(() => {
          panel.removeEventListener('transitionend', onTransitionEnd);
          finishClose();
        }, 550);
        panel.addEventListener('transitionend', onTransitionEnd);
      } else {
        finishClose();
      }
    };

    quickAddFlowGuideDialog.addEventListener('close', () => {
      quickAddFlowGuideDialog.classList.remove('is-open-animating');
      quickAddFlowGuideDialog.classList.remove('is-panel-visible');
      quickAddFlowGuideDialog.classList.remove('is-closing');
      document.body.classList.remove('drawer-open');
    });

    const closeButtons = quickAddFlowGuideDialog.querySelectorAll('[data-quick-add-flow-guide-close]');
    closeButtons.forEach((button) => {
      /** @param {Event} event */
      const handleClose = (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeQuickAddFlowGuide();
      };
      button.addEventListener('click', handleClose, { capture: true, passive: false });
      button.addEventListener('touchstart', handleClose, { capture: true, passive: false });
    });

    quickAddFlowGuideDialog.addEventListener('click', (event) => {
      if (event.target === quickAddFlowGuideDialog) {
        closeQuickAddFlowGuide();
      }
    });

    document.addEventListener(
      'touchstart',
      (event) => {
        const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
        if (!target) return;
        const trigger = target.closest('.quick-add-absorbency-flow-guide-link');
        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (!quickAddFlowGuideDialog.open) {
          quickAddFlowGuideDialog.showModal();
          requestAnimationFrame(() => {
            quickAddFlowGuideDialog.classList.add('is-open-animating');
            requestAnimationFrame(() => {
              quickAddFlowGuideDialog.classList.add('is-panel-visible');
            });
          });
        }
        document.body.classList.add('drawer-open');
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
        if (!target) return;

        const trigger = target.closest('.quick-add-absorbency-flow-guide-link');
        if (trigger) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (!quickAddFlowGuideDialog.open) {
            quickAddFlowGuideDialog.showModal();
            requestAnimationFrame(() => {
              quickAddFlowGuideDialog.classList.add('is-open-animating');
              requestAnimationFrame(() => {
                quickAddFlowGuideDialog.classList.add('is-panel-visible');
              });
            });
          }
          document.body.classList.add('drawer-open');
          return;
        }

        const closeButton = target.closest('[data-quick-add-flow-guide-close]');
        if (closeButton) {
          event.preventDefault();
          event.stopPropagation();
          closeQuickAddFlowGuide();
        }
      },
      { capture: true }
    );
  }
})();
